import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Search, Package, TrendingUp, TrendingDown, Minus,
  ChevronLeft, X, Loader2, BarChart3, Calendar,
  DollarSign, ShoppingCart, Building2, Filter, Users,
  ChevronDown, ChevronUp, Award
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiHeaders } from '../utils/apiHelpers';

const API = process.env.REACT_APP_BACKEND_URL;

// Funzione per formattare valuta in italiano
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '€0,00';
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 3
  }).format(value);
};

export default function MagazzinoPage() {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [fornitoreQuery, setFornitoreQuery] = useState('');
  const [fornitoreInput, setFornitoreInput] = useState('');
  const [searchType, setSearchType] = useState('prodotto'); // 'prodotto' o 'fornitore'
  const [categoria, setCategoria] = useState('all');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [statistiche, setStatistiche] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [showFornitoriDropdown, setShowFornitoriDropdown] = useState(false);
  
  // Ricerca avanzata prodotti con confronto prezzi
  const [advancedSearchInput, setAdvancedSearchInput] = useState('');
  const [advancedSearchResults, setAdvancedSearchResults] = useState([]);
  const [loadingAdvancedSearch, setLoadingAdvancedSearch] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});
  
  // Dettaglio prodotto
  const [selectedProdotto, setSelectedProdotto] = useState(null);
  const [storico, setStorico] = useState(null);
  const [loadingStorico, setLoadingStorico] = useState(false);

  // Usa l'helper centralizzato per gli headers
  const getAuthHeaders = getApiHeaders;

  // Carica statistiche
  const loadStatistiche = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/magazzino/statistiche`, {
        headers: getAuthHeaders()
      });
      setStatistiche(res.data);
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  }, []);

  // Carica fornitori per autocomplete
  const loadFornitori = useCallback(async (search = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const res = await axios.get(`${API}/api/magazzino/fornitori?${params}`, {
        headers: getAuthHeaders()
      });
      setFornitori(res.data.fornitori || []);
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
    }
  }, []);

  // Carica prodotti
  const loadProdotti = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (fornitoreQuery) params.append('fornitore', fornitoreQuery);
      if (categoria !== 'all') params.append('categoria', categoria);
      params.append('skip', skip);
      params.append('limit', limit);

      const res = await axios.get(`${API}/api/magazzino/prodotti?${params}`, {
        headers: getAuthHeaders()
      });
      
      setProdotti(res.data.prodotti || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
      
    } finally {
      setLoading(false);
    }
  }, [searchQuery, fornitoreQuery, categoria, skip, limit]);

  // Carica storico prodotto
  const loadStorico = async (prodottoNome) => {
    setLoadingStorico(true);
    try {
      const res = await axios.get(
        `${API}/api/magazzino/prodotti/${encodeURIComponent(prodottoNome)}/storico`,
        { headers: getAuthHeaders() }
      );
      setStorico(res.data);
    } catch (error) {
      console.error('Errore caricamento storico:', error);
      
    } finally {
      setLoadingStorico(false);
    }
  };

  // Ricerca avanzata prodotti con confronto prezzi
  const searchAdvancedProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setAdvancedSearchResults([]);
      return;
    }
    
    setLoadingAdvancedSearch(true);
    try {
      const params = new URLSearchParams();
      params.append('search', searchTerm);
      params.append('limit', '100');
      
      const res = await axios.get(`${API}/api/magazzino/prodotti/cerca?${params}`, {
        headers: getAuthHeaders()
      });
      
      setAdvancedSearchResults(res.data.prodotti || []);
    } catch (error) {
      console.error('Errore ricerca avanzata:', error);
      toast.error('Errore nella ricerca prodotti');
    } finally {
      setLoadingAdvancedSearch(false);
    }
  };

  // Toggle espansione gruppo prodotto
  const toggleProductExpand = (prodottoKey) => {
    setExpandedProducts(prev => ({
      ...prev,
      [prodottoKey]: !prev[prodottoKey]
    }));
  };

  // Debounce ricerca avanzata
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showAdvancedSearch && advancedSearchInput.length >= 2) {
        searchAdvancedProducts(advancedSearchInput);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [advancedSearchInput, showAdvancedSearch]);

  useEffect(() => {
    loadStatistiche();
    loadFornitori();
  }, [loadStatistiche, loadFornitori]);

  useEffect(() => {
    loadProdotti();
  }, [loadProdotti]);

  // Debounce search prodotto
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setSkip(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounce search fornitore
  useEffect(() => {
    const timer = setTimeout(() => {
      setFornitoreQuery(fornitoreInput);
      setSkip(0);
      // Aggiorna anche la lista fornitori per autocomplete
      if (fornitoreInput) {
        loadFornitori(fornitoreInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [fornitoreInput, loadFornitori]);

  // Seleziona fornitore da dropdown
  const selectFornitore = (nome) => {
    setFornitoreInput(nome);
    setFornitoreQuery(nome);
    setShowFornitoriDropdown(false);
    setSkip(0);
  };

  // Clear filtro fornitore
  const clearFornitore = () => {
    setFornitoreInput('');
    setFornitoreQuery('');
    setSkip(0);
  };

  // Apri dettaglio prodotto
  const openProdottoDetail = (prodotto) => {
    setSelectedProdotto(prodotto);
    // Usa l'ID (nome normalizzato) invece del nome completo per evitare problemi con caratteri speciali
    loadStorico(prodotto.id || prodotto.nome);
  };

  // Chiudi dettaglio
  const closeProdottoDetail = () => {
    setSelectedProdotto(null);
    setStorico(null);
  };

  // Calcola trend prezzo
  const getTrendIcon = (variazione) => {
    if (variazione > 0.5) return <TrendingUp className="h-4 w-4 text-red-500" />;
    if (variazione < -0.5) return <TrendingDown className="h-4 w-4 text-green-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="h-7 w-7 text-blue-600" />
                Magazzino Prodotti
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                Tutti i prodotti food e beverage dalle fatture
              </p>
            </div>
            
            {/* Stats Cards */}
            {statistiche && (
              <div className="hidden md:flex items-center gap-4">
                <div className="bg-blue-50 px-4 py-2 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">Prodotti</p>
                  <p className="text-lg font-bold text-blue-700">{statistiche.totale_prodotti?.toLocaleString()}</p>
                </div>
                <div className="bg-green-50 px-4 py-2 rounded-lg">
                  <p className="text-xs text-green-600 font-medium">Fornitori</p>
                  <p className="text-lg font-bold text-green-700">{statistiche.totale_fornitori}</p>
                </div>
                <div className="bg-purple-50 px-4 py-2 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Valore Acquisti</p>
                  <p className="text-lg font-bold text-purple-700">€{statistiche.valore_totale_acquisti?.toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Toggle tra ricerca normale e avanzata */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => setShowAdvancedSearch(false)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              !showAdvancedSearch 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="magazzino-tab-lista"
          >
            <Package className="h-4 w-4 inline mr-2" />
            Lista Prodotti
          </button>
          <button
            onClick={() => setShowAdvancedSearch(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              showAdvancedSearch 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="magazzino-tab-confronto"
          >
            <BarChart3 className="h-4 w-4 inline mr-2" />
            Confronta Prezzi
          </button>
        </div>

        {/* Ricerca Avanzata - Confronto Prezzi */}
        {showAdvancedSearch ? (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
              <input
                type="text"
                placeholder="Cerca prodotto per confrontare prezzi tra fornitori... (es: gambero, panna, pomodoro)"
                value={advancedSearchInput}
                onChange={(e) => setAdvancedSearchInput(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-lg"
                data-testid="magazzino-advanced-search"
              />
              {advancedSearchInput && (
                <button 
                  onClick={() => {
                    setAdvancedSearchInput('');
                    setAdvancedSearchResults([]);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            
            {advancedSearchInput.length >= 2 && (
              <p className="text-sm text-gray-500">
                {loadingAdvancedSearch 
                  ? 'Ricerca in corso...' 
                  : `${advancedSearchResults.length} prodotti trovati`
                }
              </p>
            )}
          </div>
        ) : (
          /* Ricerca normale */
          <div className="flex flex-col md:flex-row gap-4">
          {/* Search Prodotto */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca prodotto..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              data-testid="magazzino-search"
            />
            {searchInput && (
              <button 
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Search Fornitore */}
          <div className="flex-1 relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per fornitore..."
              value={fornitoreInput}
              onChange={(e) => {
                setFornitoreInput(e.target.value);
                setShowFornitoriDropdown(true);
              }}
              onFocus={() => setShowFornitoriDropdown(true)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
              data-testid="magazzino-search-fornitore"
            />
            {fornitoreInput && (
              <button 
                onClick={clearFornitore}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            
            {/* Dropdown Fornitori */}
            {showFornitoriDropdown && fornitoreInput && fornitori.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
                {fornitori
                  .filter(f => f.nome.toLowerCase().includes(fornitoreInput.toLowerCase()))
                  .slice(0, 10)
                  .map((f, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectFornitore(f.nome)}
                      className="w-full px-4 py-2 text-left hover:bg-green-50 flex items-center justify-between text-sm"
                    >
                      <span className="font-medium text-gray-900">{f.nome}</span>
                      <span className="text-gray-500 text-xs">{f.totale_fatture} fatture</span>
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Categoria Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={categoria}
              onChange={(e) => { setCategoria(e.target.value); setSkip(0); }}
              className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 bg-white"
              data-testid="magazzino-categoria"
            >
              <option value="all">Tutti i Prodotti</option>
              <option value="food">Solo Food</option>
              <option value="beverage">Solo Beverage</option>
            </select>
          </div>
        </div>
        )}

        {/* Filtri attivi e Results count - Solo in modalità lista */}
        {!showAdvancedSearch && (
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <span className="text-sm text-gray-500">
            {loading ? 'Caricamento...' : `${total.toLocaleString()} prodotti trovati`}
          </span>
          
          {/* Badge filtri attivi */}
          {fornitoreQuery && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              <Building2 className="h-3.5 w-3.5" />
              Fornitore: {fornitoreQuery}
              <button onClick={clearFornitore} className="ml-1 hover:text-green-900">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
          {searchQuery && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
              <Search className="h-3.5 w-3.5" />
              Prodotto: {searchQuery}
              <button onClick={() => setSearchInput('')} className="ml-1 hover:text-blue-900">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          )}
        </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {showFornitoriDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowFornitoriDropdown(false)}
        />
      )}

      {/* Advanced Search Results - Confronta Prezzi */}
      {showAdvancedSearch && (
        <div className="max-w-7xl mx-auto px-4 pb-8">
          {loadingAdvancedSearch ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : advancedSearchResults.length > 0 ? (
            <div className="space-y-4">
              {advancedSearchResults.map((gruppo, idx) => (
                <div 
                  key={idx}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                  data-testid={`search-result-${idx}`}
                >
                  {/* Header del gruppo prodotto */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleProductExpand(gruppo.nome_prodotto)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-lg">{gruppo.nome_prodotto}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="h-4 w-4" />
                            {gruppo.num_fornitori} fornitor{gruppo.num_fornitori > 1 ? 'i' : 'e'}
                          </span>
                          <span>{gruppo.num_occorrenze} acquist{gruppo.num_occorrenze > 1 ? 'i' : 'o'}</span>
                        </div>
                      </div>
                      
                      {/* Prezzo min/max */}
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Prezzo più basso</p>
                          <p className="text-lg font-bold text-green-600">{formatCurrency(gruppo.prezzo_min)}</p>
                          <p className="text-xs text-green-600">{gruppo.fornitore_min}</p>
                        </div>
                        {gruppo.prezzo_max > gruppo.prezzo_min && (
                          <div className="text-right">
                            <p className="text-xs text-gray-500">Prezzo più alto</p>
                            <p className="text-lg font-bold text-red-500">{formatCurrency(gruppo.prezzo_max)}</p>
                            <p className="text-xs text-red-500">{gruppo.fornitore_max}</p>
                          </div>
                        )}
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          {expandedProducts[gruppo.nome_prodotto] ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dettagli espansi - Lista fornitori ordinati per prezzo */}
                  {expandedProducts[gruppo.nome_prodotto] && (
                    <div className="border-t border-gray-100 bg-gray-50">
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Confronto prezzi per fornitore (dal più economico)
                        </h4>
                        <div className="space-y-2">
                          {gruppo.occorrenze?.map((occ, occIdx) => (
                            <div 
                              key={occIdx}
                              className={`flex items-center justify-between p-3 rounded-lg ${
                                occIdx === 0 
                                  ? 'bg-green-50 border border-green-200' 
                                  : occIdx === gruppo.occorrenze.length - 1 && gruppo.occorrenze.length > 1
                                    ? 'bg-red-50 border border-red-200'
                                    : 'bg-white border border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {occIdx === 0 && (
                                  <Award className="h-5 w-5 text-green-500" />
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">{occ.fornitore}</p>
                                  <p className="text-xs text-gray-500">
                                    {occ.descrizione}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    Fattura del {occ.data_fattura} - Qtà: {occ.quantita_fattura}
                                    {occ.pezzi_confezione > 1 && ` (${occ.pezzi_confezione} pz/conf)`}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`text-lg font-bold ${
                                  occIdx === 0 
                                    ? 'text-green-600' 
                                    : occIdx === gruppo.occorrenze.length - 1 && gruppo.occorrenze.length > 1
                                      ? 'text-red-500'
                                      : 'text-gray-900'
                                }`}>
                                  {formatCurrency(occ.prezzo_singolo_pezzo)}
                                  <span className="text-xs font-normal text-gray-500"> /pz</span>
                                </p>
                                {occ.pezzi_confezione > 1 && (
                                  <p className="text-xs text-gray-500">
                                    Conf: {formatCurrency(occ.prezzo_confezione)}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Risparmio potenziale */}
                        {gruppo.prezzo_max > gruppo.prezzo_min && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800">
                              <strong>Risparmio potenziale:</strong> Acquistando da <strong>{gruppo.fornitore_min}</strong> invece che da <strong>{gruppo.fornitore_max}</strong> risparmi{' '}
                              <strong className="text-green-600">
                                {formatCurrency(gruppo.prezzo_max - gruppo.prezzo_min)}
                              </strong> per pezzo
                              ({((1 - gruppo.prezzo_min / gruppo.prezzo_max) * 100).toFixed(1)}%)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : advancedSearchInput.length >= 2 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Search className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Nessun prodotto trovato per "{advancedSearchInput}"</p>
              <p className="text-sm text-gray-400 mt-1">Prova con un termine diverso</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Cerca un prodotto per confrontare i prezzi</p>
              <p className="text-sm text-gray-400 mt-1">
                Inserisci almeno 2 caratteri per iniziare la ricerca
              </p>
            </div>
          )}
        </div>
      )}

      {/* Products List - Solo in modalità lista normale */}
      {!showAdvancedSearch && (
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
                <div className="col-span-4">Prodotto</div>
                <div className="col-span-2 text-right">Prezzo Conf.</div>
                <div className="col-span-2 text-right">Prezzo Singolo</div>
                <div className="col-span-1 text-center">Acquisti</div>
                <div className="col-span-3">Fornitore</div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-100">
                {prodotti.map((prodotto, idx) => (
                  <div
                    key={prodotto.id || idx}
                    onClick={() => openProdottoDetail(prodotto)}
                    className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors items-center"
                    data-testid={`prodotto-row-${idx}`}
                  >
                    <div className="col-span-4">
                      <p className="font-medium text-gray-900 truncate">{prodotto.nome}</p>
                      <p className="text-xs text-gray-500">
                        Ultimo: {prodotto.ultima_data}
                        {prodotto.qta_confezione > 1 && (
                          <span className="ml-2 text-blue-600">({prodotto.qta_confezione} pz/conf)</span>
                        )}
                      </p>
                    </div>
                    <div className="col-span-2 text-right">
                      <p className="font-semibold text-gray-900">€{prodotto.ultimo_prezzo?.toFixed(2)}</p>
                      {prodotto.prezzo_min !== prodotto.prezzo_max && (
                        <p className="text-xs text-gray-500">
                          {prodotto.prezzo_min?.toFixed(2)} - {prodotto.prezzo_max?.toFixed(2)}
                        </p>
                      )}
                    </div>
                    <div className="col-span-2 text-right">
                      {prodotto.qta_confezione > 1 ? (
                        <>
                          <p className="font-semibold text-green-600">€{prodotto.prezzo_singolo?.toFixed(3)}</p>
                          <p className="text-xs text-gray-500">
                            (con sconto)
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {prodotto.totale_acquisti}
                      </span>
                    </div>
                    <div className="col-span-3">
                      <p className="text-sm text-gray-600 truncate flex items-center gap-1">
                        <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        {prodotto.fornitore_principale}
                      </p>
                      {prodotto.fornitori?.length > 1 && (
                        <p className="text-xs text-gray-400">
                          +{prodotto.fornitori.length - 1} altri
                        </p>
                      )}
                    </div>
                  </div>
                ))}

                {prodotti.length === 0 && (
                  <div className="px-4 py-12 text-center text-gray-500">
                    <Package className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p>Nessun prodotto trovato</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Precedenti
                </button>
                <span className="text-sm text-gray-500">
                  Pagina {Math.floor(skip / limit) + 1} di {Math.ceil(total / limit)}
                </span>
                <button
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= total}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Successivi →
                </button>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Product Detail Modal */}
      {selectedProdotto && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
              <div className="flex items-start justify-between">
                <div>
                  <button 
                    onClick={closeProdottoDetail}
                    className="text-blue-200 hover:text-white mb-2 flex items-center gap-1 text-sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Torna al magazzino
                  </button>
                  <h2 className="text-xl font-bold">{selectedProdotto.nome}</h2>
                  <p className="text-blue-200 text-sm mt-1">
                    Storico acquisti e prezzi
                  </p>
                </div>
                <button 
                  onClick={closeProdottoDetail}
                  className="p-2 hover:bg-blue-500 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {loadingStorico ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : storico ? (
                <>
                  {/* Info Confezione */}
                  {storico.statistiche?.qta_confezione > 1 && (
                    <div className="bg-blue-100 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-blue-800">
                      <strong>Confezione multipla:</strong> {storico.statistiche.qta_confezione} pezzi per confezione. 
                      I prezzi singoli sono calcolati automaticamente.
                    </div>
                  )}
                  
                  {/* Statistiche Prodotto */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-xl">
                      <div className="flex items-center gap-2 text-blue-600 mb-1">
                        <ShoppingCart className="h-4 w-4" />
                        <span className="text-xs font-medium">Tot. Acquisti</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-700">{storico.statistiche?.totale_acquisti}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl">
                      <div className="flex items-center gap-2 text-green-600 mb-1">
                        <DollarSign className="h-4 w-4" />
                        <span className="text-xs font-medium">
                          {storico.statistiche?.qta_confezione > 1 ? 'Prezzo Singolo' : 'Ultimo Prezzo'}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-green-700">
                        €{storico.statistiche?.qta_confezione > 1 
                          ? storico.statistiche?.ultimo_prezzo_singolo?.toFixed(3) 
                          : storico.statistiche?.ultimo_prezzo?.toFixed(2)}
                      </p>
                      {storico.statistiche?.qta_confezione > 1 && (
                        <p className="text-xs text-green-600">Conf: €{storico.statistiche?.ultimo_prezzo?.toFixed(2)}</p>
                      )}
                    </div>
                    <div className="bg-purple-50 p-4 rounded-xl">
                      <div className="flex items-center gap-2 text-purple-600 mb-1">
                        <BarChart3 className="h-4 w-4" />
                        <span className="text-xs font-medium">
                          {storico.statistiche?.qta_confezione > 1 ? 'Media Singolo' : 'Prezzo Medio'}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-purple-700">
                        €{storico.statistiche?.qta_confezione > 1 
                          ? storico.statistiche?.prezzo_medio_singolo?.toFixed(3) 
                          : storico.statistiche?.prezzo_medio?.toFixed(2)}
                      </p>
                      {storico.statistiche?.qta_confezione > 1 && (
                        <p className="text-xs text-purple-600">Conf: €{storico.statistiche?.prezzo_medio?.toFixed(2)}</p>
                      )}
                    </div>
                    <div className="bg-orange-50 p-4 rounded-xl">
                      <div className="flex items-center gap-2 text-orange-600 mb-1">
                        {getTrendIcon(storico.statistiche?.variazione_prezzo)}
                        <span className="text-xs font-medium">Variazione</span>
                      </div>
                      <p className={`text-2xl font-bold ${storico.statistiche?.variazione_prezzo > 0 ? 'text-red-600' : storico.statistiche?.variazione_prezzo < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                        {storico.statistiche?.variazione_prezzo > 0 ? '+' : ''}€{storico.statistiche?.variazione_prezzo?.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Range Prezzi */}
                  <div className="bg-gray-50 p-4 rounded-xl mb-6">
                    <p className="text-sm text-gray-600 mb-2">Range Prezzi (Confezione)</p>
                    <div className="flex items-center gap-4">
                      <span className="text-green-600 font-medium">Min: €{storico.statistiche?.prezzo_min?.toFixed(2)}</span>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-red-500 rounded-full"
                          style={{width: '100%'}}
                        />
                      </div>
                      <span className="text-red-600 font-medium">Max: €{storico.statistiche?.prezzo_max?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Storico Acquisti */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      Storico Acquisti
                    </h3>
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Table Header */}
                      <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 border-b">
                        <div className="col-span-2">Data</div>
                        <div className="col-span-2">Fornitore</div>
                        <div className="col-span-2 text-right">Prezzo Conf.</div>
                        <div className="col-span-2 text-right">Prezzo Sing.</div>
                        <div className="col-span-1 text-center">Qtà</div>
                        <div className="col-span-1 text-right">Sconto</div>
                        <div className="col-span-2 text-right">Pagato</div>
                      </div>
                      
                      {/* Table Body */}
                      <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                        {storico.storico?.map((item, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-gray-50">
                            <div className="col-span-2 text-gray-600">{item.data}</div>
                            <div className="col-span-2 text-gray-900 truncate" title={item.fornitore}>{item.fornitore}</div>
                            <div className="col-span-2 text-right font-medium">€{item.prezzo_unitario?.toFixed(2)}</div>
                            <div className="col-span-2 text-right">
                              {item.qta_confezione > 1 ? (
                                <span className="text-green-600 font-medium">€{item.prezzo_singolo?.toFixed(3)}</span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                            <div className="col-span-1 text-center text-gray-600">{item.quantita}</div>
                            <div className="col-span-1 text-right">
                              {item.sconto_euro > 0 ? (
                                <span className="text-green-600 text-xs">
                                  {item.sconto_percentuale?.toFixed(0)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </div>
                            <div className="col-span-2 text-right font-semibold text-blue-600">€{item.prezzo_pagato?.toFixed(2)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Totali */}
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Totale Quantità Acquistata</p>
                      <p className="text-xl font-bold text-blue-700">{storico.statistiche?.totale_quantita?.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-blue-600">Totale Speso</p>
                      <p className="text-xl font-bold text-blue-700">€{storico.statistiche?.totale_speso?.toFixed(2)}</p>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
